import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtCLP } from "../lib/api";
import { X, Upload, Loader2, Check, PartyPopper, UserCheck } from "lucide-react";
import { toast } from "sonner";

const empty = { nombre: "", apellido: "", rut: "", telefono: "", correo: "", direccion: "", observacion: "" };

export default function ReceiptForm({ open, onClose, onSuccess }) {
  const { items, subtotal, hasGift } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const fileRef = useRef();

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-fill when user is logged in and form is empty
  useEffect(() => {
    if (open && user && !form.nombre && !form.correo) {
      // don't auto-fill - leave it to "Usar mis datos" button to keep UX explicit
    }
  }, [open, user]);

  const useMyData = () => {
    if (!user) return;
    setForm({
      nombre: user.nombre || "", apellido: user.apellido || "", rut: user.rut || "",
      telefono: user.telefono || "", correo: user.correo || "", direccion: user.direccion || "",
      observacion: form.observacion || "",
    });
    toast.success("Datos cargados desde tu cuenta");
  };

  const onPickReceipt = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Comprobante máximo 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setReceipt(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const required = ["nombre", "apellido", "rut", "telefono", "correo", "direccion"];
    for (const k of required) {
      if (!form[k]?.trim()) { toast.error(`Completa el campo: ${labelOf(k)}`); return; }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.correo)) { toast.error("Correo inválido"); return; }
    if (!receipt) { toast.error("Sube el comprobante para continuar"); return; }
    if (items.length === 0) { toast.error("Tu carrito está vacío"); return; }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity,
          image: i.image, custom_image: i.custom_image, custom_text: i.custom_text, phone_model: i.phone_model,
        })),
        subtotal, total: subtotal, has_gift: hasGift,
        customer: form,
        receipt_image: receipt,
        user_id: user?.id || null,
      };
      const { data } = await api.post("/orders", payload);
      setOrderNumber(data.order_number);
    } catch {
      toast.error("No se pudo enviar el pedido");
    } finally { setSubmitting(false); }
  };

  const handleClose = () => {
    if (orderNumber) {
      onSuccess?.();
      setOrderNumber(null);
      setForm(empty);
      setReceipt(null);
    }
    onClose();
  };

  const onWhatsApp = () => {
    const msg = `Hola, hice el pedido ${orderNumber} y necesito más información.`;
    window.open(`https://wa.me/56975359774?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="receipt-form-dialog">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-sky-50 to-white">
          <div>
            <div className="font-heading font-black text-xl">
              {orderNumber ? "¡Pedido recibido!" : "Cargar comprobante y datos"}
            </div>
            {!orderNumber && <div className="text-xs text-slate-500">Adjunta tu comprobante y completa la dirección de envío</div>}
          </div>
          <button onClick={handleClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="receipt-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
          {orderNumber ? (
            <div className="text-center py-8" data-testid="order-success">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4 tech-glow">
                <PartyPopper size={36} />
              </div>
              <h3 className="font-heading font-black text-3xl text-black">¡Tu pedido se recibió con éxito! 🎉</h3>
              <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
                Pedido <strong className="text-[#0EA5E9]">{orderNumber}</strong>. Nos comunicaremos contigo en las próximas horas. Si deseas más información, escríbenos por WhatsApp.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button onClick={onWhatsApp} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-full font-bold inline-flex items-center gap-2" data-testid="success-whatsapp-btn">
                  <i className="fa-brands fa-whatsapp text-lg" /> WhatsApp
                </button>
                <button onClick={handleClose} className="btn-outline-dark inline-flex items-center gap-2" data-testid="success-close-btn">
                  Seguir comprando
                </button>
              </div>
            </div>
          ) : (
            <>
              {user && (
                <button
                  onClick={useMyData}
                  className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-dashed border-[#0EA5E9] bg-sky-50 text-[#0EA5E9] font-bold inline-flex items-center justify-center gap-2 hover:bg-sky-100 transition"
                  data-testid="use-my-data-btn"
                >
                  <UserCheck size={18} /> Usar mis datos ({user.nombre} {user.apellido})
                </button>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre" id="nombre" value={form.nombre} onChange={(v) => setF("nombre", v)} />
                <Field label="Apellido" id="apellido" value={form.apellido} onChange={(v) => setF("apellido", v)} />
                <Field label="RUT" id="rut" value={form.rut} onChange={(v) => setF("rut", v)} placeholder="12.345.678-9" />
                <Field label="Teléfono" id="telefono" value={form.telefono} onChange={(v) => setF("telefono", v)} placeholder="+56 9 1234 5678" />
                <Field label="Correo" id="correo" type="email" value={form.correo} onChange={(v) => setF("correo", v)} className="col-span-2" />
                <Field label="Dirección completa" id="direccion" value={form.direccion} onChange={(v) => setF("direccion", v)} className="col-span-2" />
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">Observación</label>
                  <textarea
                    rows={2} value={form.observacion} onChange={(e) => setF("observacion", e.target.value)}
                    data-testid="receipt-input-observacion"
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] outline-none"
                    placeholder="Color preferido, urgencia, etc."
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">Comprobante de pago</label>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickReceipt} data-testid="receipt-file-input" />
                {receipt ? (
                  <div className="relative">
                    <img src={receipt} alt="comprobante" className="w-full max-h-64 object-contain rounded-2xl border border-slate-200 bg-slate-50" />
                    <button onClick={() => setReceipt(null)} className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow" data-testid="remove-receipt">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 hover:border-[#0EA5E9] hover:bg-sky-50 rounded-2xl py-8 flex flex-col items-center gap-2 text-slate-500 transition" data-testid="upload-receipt-btn">
                    <Upload size={28} />
                    <div className="font-semibold">Subir captura del comprobante</div>
                    <div className="text-xs">JPG, PNG · máx 5MB</div>
                  </button>
                )}
              </div>

              <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 flex justify-between items-center">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Total a confirmar</div>
                  <div className="text-xs text-slate-500">{items.length} producto(s){hasGift ? " · 🎁 con regalo" : ""}</div>
                </div>
                <div className="font-heading font-black text-2xl">{fmtCLP(subtotal)}</div>
              </div>
            </>
          )}
        </div>

        {!orderNumber && (
          <div className="border-t px-6 py-4 bg-slate-50 flex justify-end">
            <button onClick={submit} disabled={submitting} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="submit-receipt-btn">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Enviando...</> : <>Enviar pedido <Check size={16} /></>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, id, value, onChange, type = "text", placeholder = "", className = "" }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">{label}</label>
    <input
      type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`receipt-input-${id}`}
      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none font-medium"
    />
  </div>
);

const labelOf = (k) => ({
  nombre: "Nombre", apellido: "Apellido", rut: "RUT", telefono: "Teléfono", correo: "Correo", direccion: "Dirección"
}[k] || k);
